import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useHistory, useParams } from "react-router-dom";
import { authSelectors } from "../../store/auth/selector";
import { useDealQuery } from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { BotIcon, EyeIcon } from "lucide-react";

import CompanyLogo from "../CompanyLogo";
import { actions as authActions } from "../../store/auth/slice";
import { actions as assistantActions } from "../../store/assistant/slice";
import { Avatar } from "../account/Avatar";
import { PermissionCheck } from "../permissions/PermissionCheck";
import { useInvalidateQueryKeys } from "@/src/hooks/useInvalidateQueryKeys";

export function DealNavigation(props: {
  showAssistant: boolean;
  setShowAssistant: (show: boolean) => void;
  withPanelOpen: boolean;
}) {
  const location = useLocation();

  const client = useGqlClient();
  const queryClient = useQueryClient();
  const { dealId: activeDealId } = useParams<{ dealId: string }>();
  const activeDealQuery = useDealQuery(
    client,
    {
      id: activeDealId,
    },
    {
      enabled: activeDealId !== "",
    },
  );

  const { invalidatePermissionChecks } = useInvalidateQueryKeys();

  const actor = useSelector(authSelectors.actor);
  const dispatch = useDispatch();
  const history = useHistory();

  const previousActorRef = useRef(actor);
  const dealQuery = useDealQuery(
    client,
    {
      id: activeDealId,
    },
    {
      enabled: activeDealId !== "",
    },
  );

  const previousDealIdRef = useRef<string | undefined>(activeDealId);

  // Clear assistant state when the deal changes
  useEffect(() => {
    if (
      previousDealIdRef.current &&
      previousDealIdRef.current !== activeDealId
    ) {
      dispatch(assistantActions.clearAssistantState());
    }
    previousDealIdRef.current = activeDealId;
  }, [activeDealId, dispatch]);

  useEffect(() => {
    if (previousActorRef.current && !actor) {
      const queries = queryClient.getQueryCache().findAll({
        queryKey: ["ActiveDealAccount"],
      });
      for (const query of queries) {
        queryClient.invalidateQueries({ queryKey: query.queryKey });
      }
    }
    previousActorRef.current = actor;
  }, [actor, queryClient]);

  if (!location.pathname.startsWith("/deal")) {
    return null;
  }

  if (
    activeDealQuery.isPending ||
    !activeDealQuery.data ||
    dealQuery.isPending ||
    !dealQuery.data
  ) {
    return null;
  }

  const activeDeal = activeDealQuery.data;

  return (
    <div className="sticky top-0 z-20 min-w-0">
      {actor ? (
        <div className="w-full h-12 bg-persian-950/80 px-4 flex items-center justify-center gap-x-2">
          <div className="flex items-center gap-x-3">
            <EyeIcon className="w-4 h-4 text-white" />
            <p className="text-sm flex gap-x-2  items-center text-white font-semibold">
              Viewing deal as
              <span>
                <Avatar account={actor.account} size="xs" />
              </span>
              {actor.account.name}
            </p>
          </div>
          <button
            className="px-2 py-1 rounded-md shadow-sm bg-persian-500/90 hover:bg-persian-600 hover:border-persian-500 border border-persian-700 text-sm font-semibold text-white"
            onClick={() => {
              dispatch(authActions.setActor({ actor: undefined }));
              setTimeout(() => {
                queryClient.invalidateQueries({
                  queryKey: ["ActiveDealAccountRole", { id: activeDealId }],
                });
                queryClient.invalidateQueries({
                  queryKey: ["ActiveDealAccount", { id: activeDealId }],
                });
                invalidatePermissionChecks();
                history.push(
                  `/deal/${activeDealId}/access/account/${actor.dealAccountId}`,
                );
              }, 100);
            }}
          >
            Stop viewing as {actor.account.name}
          </button>
        </div>
      ) : null}
      <div className="transition-colors duration-300 ease-in-out bg-persian-50 py-4 px-6 pb-0 md:pb-0">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-3 ">
            <div style={{ position: "relative" }}>
              <CompanyLogo
                logo={activeDeal ? activeDeal.deal.company.logo : undefined}
                withShadow={false}
                borderSize={"2"}
                size={32}
                name={activeDeal ? activeDeal.deal.company.name : ""}
                bgColor={activeDeal ? activeDeal.deal.company.logoColor : "#000"}
              />
            </div>
            <div className="flex flex-col">
              <p className="font-bold text-gray-800 truncate">
                {activeDeal ? activeDeal.deal.company.name : ""}
              </p>
            </div>
          </div>

          <PermissionCheck
            input={{ permissionKey: "deal_assistant:has_access" }}
            fallback={<div></div>}
          >
            {!props.showAssistant ? (
              <button
                onClick={() => {
                  props.setShowAssistant(true);
                }}
                className="group rounded-full flex items-center gap-x-2 font-semibold  ring-persian-700 bg-gradient-to-tr from-persian-950 hover:from-persian-990 to-persian-800 hover:to-persian-900 text-sm text-white px-2.5  py-2"
              >
                <BotIcon className="w-4 h-4 text-persian-200 group-hover:text-persian-500" />
                Assistant
              </button>
            ) : (
              <div
                aria-hidden="true"
                className="rounded-md flex items-center gap-x-2 font-semibold ring-1 ring-persian-500 bg-gradient-to-tr from-persian-800 to-persian-600 text-sm text-white px-2.5 py-2 shadow-persian-400 shadow-md opacity-0 pointer-events-none"
              >
                <BotIcon className="w-4 h-4 text-persian-200 group-hover:text-persian-500" />
                Assistant
              </div>
            )}
          </PermissionCheck>
        </div>
      </div>
    </div>
  );
}

function SecondaryNavBar(props: { children: React.ReactNode }) {
  return (
    <div className="bg-gray-100 border-y border-gray-300 gap-x-8 py-0  flex items-center px-4 md:px-7">
      {props.children}
    </div>
  );
}
